// @ts-nocheck

import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDidUpdate, usePrevious } from 'sonata-react-components';

import { getCartographyFilter } from '@/helpers/reporting.helpers';
import { getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import { getHasInsightsPermissions, getInsightsConfig } from '@/store/selectors/reporting.selectors';
import {
  getCampaigns,
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getCurrency,
  getTimeFrame,
  getTimeZone
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCountry, getUserId } from '@/store/selectors/user.selectors';
import ReportingInsightsGroupItem from '../ReportingInsightsGroupItem/ReportingInsightsGroupItem';

function ReportingInsightsSection({ filterSection }) {
  const intl = useIntl();

  const { beginDate, endDate } = useSelector(store => getTimeFrame(store, filterSection));
  const baseCurrency = useSelector(getBaseCurrencyName);
  const currency = useSelector(store => getCurrency(store, filterSection));
  const timezone = useSelector(store => getTimeZone(store, filterSection));
  const userId = useSelector(getUserId);
  const { config, filters } = useSelector(getInsightsConfig);
  const hasInsightsPermission = useSelector(getHasInsightsPermissions);
  const generalCartographyCountries = useSelector(store => getCartographyCountries(store, 'general'));
  const generalCartographyLevel = useSelector(store => getCartographyLevel(store, 'general'));
  const generalCartographyLevelIds = useSelector(store => getCartographyLevelIds(store, 'general'));
  const cartographyCountries = useSelector(store => getCartographyCountries(store, filterSection));
  const cartographyLevel = useSelector(store => getCartographyLevel(store, filterSection));
  const cartographyLevelIds = useSelector(store => getCartographyLevelIds(store, filterSection));
  const country = useSelector(getCountry);
  const cIds = useSelector(getCampaigns);

  const cartographyFilter = getCartographyFilter(
    {
      cartographyCountries: generalCartographyCountries,
      cartographyLevel: generalCartographyLevel,
      cartographyLevelIds: generalCartographyLevelIds
    },
    {
      cartographyCountries,
      cartographyLevel,
      cartographyLevelIds
    }
  );

  const dateInterval = useMemo(
    () => ({
      beginDate,
      endDate,
      timezone
    }),
    [beginDate, endDate, timezone]
  );

  const [{ campaignInfo, filterItems, insightsConfig }, setState] = useState({
    campaignInfo: {
      cIds,
      cartographyFilter,
      currencyOptions: {
        baseCurrency,
        currency
      },
      dateInterval,
      userId
    },
    filterItems: filters.map(filter => ({
      ...filter,
      label: intl.formatMessage({ id: filter.label })
    })),
    insightsConfig: config
  });

  const prevFilters = usePrevious(filters);
  useDidUpdate(() => {
    if (!isEqual(prevFilters, filters)) {
      setState(prevState => ({
        ...prevState,
        filterItems: filters.map(filter => ({
          ...filter,
          label: intl.formatMessage({ id: filter.label })
        }))
      }));
    }
  }, [filters]);

  const prevConfig = usePrevious(config);
  useDidUpdate(() => {
    if (!isEqual(prevConfig, config)) {
      setState(prevState => ({
        ...prevState,
        insightsConfig: config
      }));
    }
  }, [config]);

  const prevCartographyFilter = usePrevious(cartographyFilter);
  useDidUpdate(() => {
    if (!isEqual(prevCartographyFilter, cartographyFilter)) {
      setState(prevState => ({
        ...prevState,
        campaignInfo: {
          ...prevState.campaignInfo,
          cartographyFilter
        }
      }));
    }
  }, [cartographyFilter]);

  const prevDateInterval = usePrevious(dateInterval);
  useDidUpdate(() => {
    if (!isEqual(prevDateInterval, dateInterval)) {
      setState(prevState => ({
        ...prevState,
        campaignInfo: {
          ...prevState.campaignInfo,
          dateInterval
        }
      }));
    }
  }, [dateInterval]);

  return (
    <div className="reportingInsightsSection">
      {hasInsightsPermission && (
        <ReportingInsightsGroupItem
          campaignInfo={campaignInfo}
          config={insightsConfig}
          country={country}
          filterSection={filterSection}
          items={filterItems}
          section="reportingInsights"
        />
      )}
    </div>
  );
}

ReportingInsightsSection.propTypes = {
  filterSection: PropTypes.string.isRequired
};

export default React.memo(ReportingInsightsSection);
